import { SVTA_UNSUPPORTED_AUDIO_FORMAT, SVTA_UNSUPPORTED_DRM_SYSTEM, SVTA_UNSUPPORTED_ENCRYPTION_METHOD, SVTA_UNSUPPORTED_VIDEO_FORMAT } from "../../../media/errors.js";
//#region src/playback/adapters/hls-video/error-surface.ts
/**
* `message`, plus the alternative-Media sentence when `media`'s class names one.
*
* Read off the class rather than passed in so a subclass can point at a better-equipped sibling — the SPF Mux Medias
* name the hls.js-backed one, which plays the MPEG-TS and DRM sources SPF doesn't — without either adapter knowing that
* sibling exists.
*/
function withAlternativeMediaSuggestion(message, media) {
	const { alternativeMediaSuggestion } = media.constructor;
	const suggestion = alternativeMediaSuggestion?.trim();
	return suggestion ? `${message} ${suggestion}` : message;
}
/** The first condition `fatalCodes` covers — the root cause, not its consequences. */
function firstFatal(errors, fatalCodes) {
	return errors?.find((error) => fatalCodes.has(error.code));
}
/**
* The causes that mean the engine has no pipeline for what the source delivers — a container it can't append, or
* encryption it can't decrypt.
*
* What these have in common is that no retry, no other CDN, and no other rendition of the same source fixes them: the
* source needs a capability this engine doesn't have. That's the distinction the surfaced code exists to draw, and it's
* why the set is these rather than "every cause": an undecodable container (1004/1005), a DRM system with no CDM
* (4008), or a non-DRM encryption scheme with no decryptor (99408).
*/
const UNSUPPORTED_FEATURE_CAUSES = /* @__PURE__ */ new Set([
	SVTA_UNSUPPORTED_VIDEO_FORMAT,
	SVTA_UNSUPPORTED_AUDIO_FORMAT,
	SVTA_UNSUPPORTED_DRM_SYSTEM,
	SVTA_UNSUPPORTED_ENCRYPTION_METHOD
]);
/**
* Whether anything in the sequence is a cause of the "we don't implement this" kind.
*
* Deliberately `some` over the whole sequence rather than a per-type match against the verdict. A verdict means one
* type's candidates emptied, but the _reason_ the source is unplayable can sit on another type — an audio-only source
* whose sole rendition is encrypted empties the audio candidates, and a video source with encrypted video empties the
* video ones. Both are the same answer to the viewer, so both get the same code.
*/
function hasUnsupportedFeatureCause(errors) {
	return errors?.some((error) => UNSUPPORTED_FEATURE_CAUSES.has(error.code)) ?? false;
}
//#endregion
export { firstFatal, hasUnsupportedFeatureCause, withAlternativeMediaSuggestion };

//# sourceMappingURL=error-surface.js.map